import { createAsyncThunk, createSlice, SerializedError } from '@reduxjs/toolkit'
import { LoadingState, Redux } from '../..'

export interface CronjobDefinition {
  id: string
  methodName: string
  cronExpression: string
  timeZone: string | null
  name: string
  schedule: string
  isPaused: boolean
}

type StateSlice<T> = {
  value: T
  loadingState: LoadingState
  error: SerializedError | null
}

interface CronjobsState {
  cronjobs: StateSlice<CronjobDefinition[]>
  selectedCronjob: StateSlice<CronjobDefinition | null>
}

type InitialStateType<K> = K extends StateSlice<infer T> ? T : never

const getInitialStateFor = <K>(initialValue: InitialStateType<K>): StateSlice<InitialStateType<K>> => ({
  value: initialValue,
  loadingState: 'initial',
  error: null
})

const initialState: CronjobsState = {
  cronjobs: getInitialStateFor<CronjobsState['cronjobs']>([]),
  selectedCronjob: getInitialStateFor<CronjobsState['selectedCronjob']>(null)
}

const normalizeCronjobsResponse = (payload: any): CronjobDefinition[] => {
  if (Array.isArray(payload)) {
    return payload
  }

  if (Array.isArray(payload?.data)) {
    return payload.data
  }

  if (Array.isArray(payload?.data?.data)) {
    return payload.data.data
  }

  return []
}

const normalizeCronjobResponse = (payload: any): CronjobDefinition | null => {
  if (!payload) {
    return null
  }

  if (payload.data?.data) {
    return payload.data.data
  }

  if (payload.data) {
    return payload.data
  }

  return payload
}

export const fetchCronjobs = createAsyncThunk<CronjobDefinition[], void, Redux>(
  'cronjobs/fetchAll',
  async (_, { extra }) => {
    const response = await extra.httpClient.get('cronjobs')

    return normalizeCronjobsResponse(response.data)
  }
)

export const fetchCronjobById = createAsyncThunk<CronjobDefinition | null, string, Redux>(
  'cronjobs/fetchOne',
  async (id, { extra }) => {
    const response = await extra.httpClient.get(`cronjobs/${id}`)

    return normalizeCronjobResponse(response.data)
  }
)

export const pauseCronjob = createAsyncThunk<CronjobDefinition | null, string, Redux>(
  'cronjobs/pause',
  async (id, { extra }) => {
    const response = await extra.httpClient.patch(`cronjobs/${id}/pause`)

    return normalizeCronjobResponse(response.data)
  }
)

export const resumeCronjob = createAsyncThunk<CronjobDefinition | null, string, Redux>(
  'cronjobs/resume',
  async (id, { extra }) => {
    const response = await extra.httpClient.patch(`cronjobs/${id}/resume`)

    return normalizeCronjobResponse(response.data)
  }
)

export const executeCronjob = createAsyncThunk<void, string, Redux>('cronjobs/execute', async (id, { extra }) => {
  await extra.httpClient.post(`cronjobs/execute/${id}`)
})

const patchCronjobInList = (cronjobs: CronjobDefinition[], updatedCronjob: CronjobDefinition): CronjobDefinition[] =>
  cronjobs.map(cronjob => (cronjob.id === updatedCronjob.id ? updatedCronjob : cronjob))

const cronjobsSlice = createSlice({
  name: 'cronjobs',
  initialState,
  reducers: {
    clearSelectedCronjob: state => {
      state.selectedCronjob = initialState.selectedCronjob
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchCronjobs.pending, state => {
      state.cronjobs.loadingState = 'pending'
      state.cronjobs.error = null
    })
    builder.addCase(fetchCronjobs.fulfilled, (state, action) => {
      state.cronjobs.loadingState = 'success'
      state.cronjobs.value = action.payload
    })
    builder.addCase(fetchCronjobs.rejected, (state, action) => {
      state.cronjobs.loadingState = 'error'
      state.cronjobs.error = action.error
    })

    builder.addCase(fetchCronjobById.pending, state => {
      state.selectedCronjob.loadingState = 'pending'
      state.selectedCronjob.error = null
    })
    builder.addCase(fetchCronjobById.fulfilled, (state, action) => {
      state.selectedCronjob.loadingState = 'success'
      state.selectedCronjob.value = action.payload
    })
    builder.addCase(fetchCronjobById.rejected, (state, action) => {
      state.selectedCronjob.loadingState = 'error'
      state.selectedCronjob.error = action.error
    })

    builder.addCase(pauseCronjob.fulfilled, (state, action) => {
      if (!action.payload) {
        return
      }

      state.cronjobs.value = patchCronjobInList(state.cronjobs.value, action.payload)

      if (state.selectedCronjob.value?.id === action.payload.id) {
        state.selectedCronjob.value = action.payload
      }
    })

    builder.addCase(resumeCronjob.fulfilled, (state, action) => {
      if (!action.payload) {
        return
      }

      state.cronjobs.value = patchCronjobInList(state.cronjobs.value, action.payload)

      if (state.selectedCronjob.value?.id === action.payload.id) {
        state.selectedCronjob.value = action.payload
      }
    })
  }
})

export const { clearSelectedCronjob } = cronjobsSlice.actions

export default cronjobsSlice.reducer
